import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@etherisc/ui-kit';
import { UserForm } from '../../components/UserForm';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import type { UserFormData } from '../../schemas/userSchema';
import { userService } from '../../services/userService';
import { roleService } from '../../services/roleService';
import {
  ChevronLeftIcon,
} from 'lucide-react';

export function CreateUserPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const showSuccess = (message: string, description?: string) => toast.success(message, { description });
  const showError = (message: string, description?: string) => toast.error(message, { description });
  const [loading, setLoading] = useState(false);
  
  // Check if user can assign roles (has full control) or just create users
  const canAssignRoles = hasPermission('users:assign_roles');
  const isAdmin = user?.roles?.includes('admin') ?? false;
  const isProductAdmin = user?.roles?.includes('product_admin') ?? false;
  
  // Fetch organizations from database (only for admins)
  // Product admins can only create users in their own org, so we use their org directly
  const { 
    organizations, 
    loading: organizationsLoading, 
    error: organizationsError 
  } = useOrganizations({ limit: 100, autoFetch: isAdmin }); // Only fetch for admins

  // Fetch all roles from backend (includes custom roles)
  const [allRoleNames, setAllRoleNames] = useState<string[]>([]);
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const roles = await roleService.getRoles();
        setAllRoleNames(roles.map(r => r.name));
      } catch {
        setAllRoleNames(['admin', 'system_operator', 'product_admin', 'reinsurer', 'user']);
      }
    };
    loadRoles();
  }, []);

  // Filter available roles based on current user's permissions
  const availableRoles = useMemo(() => {
    const roleNames = allRoleNames.length > 0
      ? allRoleNames
      : ['admin', 'system_operator', 'product_admin', 'reinsurer', 'user'];

    // Admin and users with assign_roles permission can assign all roles
    if (isAdmin || canAssignRoles) {
      return roleNames;
    }
    
    // Product admin can only assign 'user' role
    if (isProductAdmin) {
      return ['user'];
    }
    
    // Default: only allow assigning 'user' role
    return ['user'];
  }, [isAdmin, isProductAdmin, canAssignRoles, allRoleNames]);

  // Filter organizations based on current user's role
  const availableOrganizations = useMemo(() => {
    // Admin sees all organizations from API
    if (isAdmin) {
      return organizations.map(org => ({
        id: org.id,
        name: org.name
      }));
    }
    
    // Product admin can only create users in their own organization
    // Don't fetch from API, just use their own org
    if (isProductAdmin && user?.organization) {
      return [{
        id: user.organization.id,
        name: user.organization.name
      }];
    }
    
    // Default: show all
    return organizations.map(org => ({
      id: org.id,
      name: org.name
    }));
  }, [organizations, isAdmin, isProductAdmin, user?.organization]);

  const handleSubmit = async (data: UserFormData) => {
    setLoading(true);
    try {
      await userService.createUser(data);
      showSuccess('User Created', `User ${data.name} has been successfully created.`);
      navigate('/admin/users');
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create user. Please try again.';
      showError('Creation Failed', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/admin/users')}
          className="p-2 text-gray-500 hover:text-gray-700 rounded-md"
        >
          <ChevronLeftIcon size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New User</h1>
          <p className="text-gray-600 mt-1">
            Add a new user to the system with appropriate roles and permissions.
          </p>
        </div>
      </div>

      <UserForm
        mode="create"
        onSubmit={handleSubmit}
        loading={loading || organizationsLoading}
        availableRoles={availableRoles}
        availableOrganizations={availableOrganizations}
        organizationsError={organizationsError}
      />
    </div>
  );
} 